/*
 * hyperion-index:begin
 * ---
 * path: hyperion-cloud/web/src/app/config/destinations.ts
 * layer: web
 * area: config
 * kind: source
 * web_root: config
 * relpath: web/src/app/config/destinations.ts
 * filename: destinations.ts
 * extension: ts
 * ---
 * hyperion-index:end
 */

// The 5 destinations — closed set per navigation-model.md §2 and execution-rules.md Rule 8.
// Destination colors track the existing Tailwind palette (amber/violet/teal/emerald/slate)
// and map to the same accent mnemonic the user knows from the old sidebar groups.

import type { ComponentType } from 'react';
import {
  LibraryMark,
  PortfolioMark,
  RunwayMark,
  WorkbenchMark,
} from '@/platform/design-system/components/icons/DestinationIcons';
import { SwitchboardIcon } from '@/platform/design-system/components/icons/SwitchboardIcon';

export type DestinationId = 'workbench' | 'runway' | 'portfolio' | 'library' | 'switchboard';

export type DestinationAccent = 'amber' | 'violet' | 'blue' | 'emerald' | 'slate';

export interface Destination {
  id: DestinationId;
  label: string;
  accent: DestinationAccent;
  icon: ComponentType<{ className?: string }>;
  /** Path segment that owns this destination. Every URL starting with `/${basePath}` belongs to it. */
  basePath: string;
  /** Landing sub-menu URL when the user clicks the destination button fresh. */
  landingUrl: string;
  /**
   * Feature flag that governs destination visibility. When the flag is off:
   * (a) the destination button is not rendered
   * (b) routes under `basePath` redirect to Workbench Queue with a toast
   * Always-on destinations omit this field.
   */
  visibleWhenFlag?: string;
  /** Short marketing/description string shown in command palette results. */
  description: string;
}

/**
 * The 5 destinations in display order (top-to-bottom in the sidebar).
 * Workbench first because it's the daily-driver queue; Switchboard last
 * because it finds and configures the machinery that feeds every surface.
 */
export const DESTINATIONS: readonly Destination[] = [
  {
    id: 'workbench',
    label: 'Workbench',
    accent: 'amber',
    icon: WorkbenchMark,
    basePath: 'workbench',
    landingUrl: '/workbench?view=queue',
    description: 'Your queue — tickets, alerts, handoffs, approvals, owed time.',
  },
  {
    id: 'runway',
    label: 'Runway',
    accent: 'violet',
    icon: RunwayMark,
    basePath: 'runway',
    landingUrl: '/runway',
    visibleWhenFlag: 'runway_enabled',
    description: 'In-flight multi-step deliveries across customers.',
  },
  {
    id: 'portfolio',
    label: 'Portfolio',
    accent: 'blue',
    icon: PortfolioMark,
    basePath: 'portfolio',
    landingUrl: '/portfolio',
    description: 'Clients, analytics, reports, health — the business view.',
  },
  {
    id: 'library',
    label: 'Library',
    accent: 'emerald',
    icon: LibraryMark,
    basePath: 'library',
    landingUrl: '/library',
    visibleWhenFlag: 'library_enabled',
    description: 'Containers, templates, blocks, builders — what we can do.',
  },
  {
    id: 'switchboard',
    label: 'Switchboard',
    accent: 'slate',
    icon: SwitchboardIcon,
    basePath: 'switchboard',
    landingUrl: '/switchboard',
    description: 'Find any tool; configure integrations, staff, vault, and platform.',
  },
] as const;

/** Fast lookup by id. */
export const DESTINATION_BY_ID: Record<DestinationId, Destination> = DESTINATIONS.reduce(
  (acc, d) => {
    acc[d.id] = d;
    return acc;
  },
  {} as Record<DestinationId, Destination>
);

/** Fast lookup by the first URL path segment. */
const DESTINATION_BY_BASE_PATH = new Map(
  DESTINATIONS.map((destination) => [destination.basePath, destination] as const)
);

/**
 * Derive a destination from a URL pathname. Any path that doesn't match a
 * destination basePath resolves to `workbench` — the default landing.
 *
 * Legacy paths (e.g. `/tickets`, `/devices`, `/settings`) are expected to be
 * wrapped in compatibility redirects or assigned a disposition hint, so this
 * function should only be called with paths that have a destination home.
 *
 * Drill-in routes (ticket detail, customer detail) pass their owning
 * destination explicitly via AppShell's `destinationHint` prop.
 */
export function deriveDestinationFromPath(pathname: string): DestinationId {
  const segment = pathname.split('/').filter(Boolean)[0];
  if (!segment) return 'workbench';
  const match = DESTINATION_BY_BASE_PATH.get(segment);
  return match ? match.id : 'workbench';
}

/** Tailwind accent class root for a destination (e.g. `amber`, `emerald`). */
export function accentForDestination(id: DestinationId): DestinationAccent {
  return DESTINATION_BY_ID[id].accent;
}
